import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Sparkles,
  Loader2,
  Award,
  GraduationCap,
  Wand2,
  ArrowLeft,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  CvAnalysisResult,
  CvModel,
  CvCertification,
  CvLearning,
  CvExperience,
  CvProject,
  CvAchievement,
  CvEducation,
} from '../types/userCv';
import { analyzeFromConnects, applyCvUpdate } from '../utils/userCvApi';
import { useApiClient } from '../hooks/useApiClient';

interface SourceConnect {
  id: number;
  label: string;
}

interface Props {
  employeeId: string;
  connects: SourceConnect[];
  onClose: () => void;
  onSuccess: (newVersion: number) => void;
}

const emptyExp: CvExperience = { role: '', company: '', startDate: '', endDate: '', bullets: [] };
const emptyProject: CvProject = { name: '', role: '', description: '', techStack: [], startDate: '', endDate: '', bullets: [] };
const emptyCert: CvCertification = { name: '', issuer: '', description: '', credentialId: '', date: '' };
const emptyLearn: CvLearning = { title: '', description: '', date: '' };
const emptyEdu: CvEducation = { degree: '', institution: '', location: '', score: '', year: '' };
const emptyAchievement: CvAchievement = { title: '', description: '', date: '' };

/**
 * Full-screen Outlook-style master/detail panel.
 * Left  = AI suggestions with include/exclude checkboxes (drives what's added).
 * Right = full editable CV preview (user can tweak before saving as a new version).
 */
export function CvUpdateReviewPanel({ employeeId, connects, onClose, onSuccess }: Props) {
  const { apiCall } = useApiClient();
  const connectIds = useMemo(() => connects.map(c => c.id), [connects]);

  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<CvAnalysisResult | null>(null);

  // Sets of indices of AI-suggested items currently included
  const [skillSel, setSkillSel] = useState<Set<number>>(new Set());
  const [certSel, setCertSel] = useState<Set<number>>(new Set());
  const [learnSel, setLearnSel] = useState<Set<number>>(new Set());

  // Editable working CV (right pane)
  const [draft, setDraft] = useState<CvModel | null>(null);
  // Track which experience cards are expanded
  const [expandedExp, setExpandedExp] = useState<Set<number>>(new Set([0]));
  // Track which project cards are expanded
  const [expandedProj, setExpandedProj] = useState<Set<number>>(new Set([0]));

  // Snapshot of original (non-AI) CV so we can recompute when a checkbox toggles
  const baselineRef = useRef<{
    skills: string[];
    certifications: CvCertification[];
    learnings: CvLearning[];
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await analyzeFromConnects(employeeId, connectIds, apiCall);
        if (cancelled) return;
        setAnalysis(result);
        const newSkillsLower = new Set((result.newSkills ?? []).map(s => s.toLowerCase()));
        const newCertNames = new Set((result.newCertifications ?? []).map(c => c.name.toLowerCase()));
        const newLearnTitles = new Set((result.newLearnings ?? []).map(l => l.title.toLowerCase()));
        const updated = result.updatedCv;
        // Baseline = updatedCv minus the AI-added items (so we can re-add per selection)
        baselineRef.current = {
          skills: (updated?.skills ?? []).filter(s => !newSkillsLower.has(s.toLowerCase())),
          certifications: (updated?.certifications ?? []).filter(c => !newCertNames.has(c.name.toLowerCase())),
          learnings: (updated?.learnings ?? []).filter(l => !newLearnTitles.has(l.title.toLowerCase())),
        };
        setSkillSel(new Set((result.newSkills ?? []).map((_, i) => i)));
        setCertSel(new Set((result.newCertifications ?? []).map((_, i) => i)));
        setLearnSel(new Set((result.newLearnings ?? []).map((_, i) => i)));
        setDraft(updated ? structuredClone(updated) : null);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'AI analysis failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, connectIds.join(',')]);

  // When a left-side checkbox toggles, splice the AI item in/out of the right-pane draft
  // while preserving any manual user edits elsewhere.
  const toggleSkill = (idx: number) => {
    if (!analysis || !draft) return;
    const item = analysis.newSkills?.[idx];
    if (!item) return;
    const next = new Set(skillSel);
    const include = !next.has(idx);
    if (include) next.add(idx); else next.delete(idx);
    setSkillSel(next);
    setDraft(d => {
      if (!d) return d;
      const present = (d.skills ?? []).some(s => s.toLowerCase() === item.toLowerCase());
      if (include && !present) return { ...d, skills: [...(d.skills ?? []), item] };
      if (!include && present) return { ...d, skills: (d.skills ?? []).filter(s => s.toLowerCase() !== item.toLowerCase()) };
      return d;
    });
  };

  const toggleCert = (idx: number) => {
    if (!analysis || !draft) return;
    const item = analysis.newCertifications?.[idx];
    if (!item) return;
    const next = new Set(certSel);
    const include = !next.has(idx);
    if (include) next.add(idx); else next.delete(idx);
    setCertSel(next);
    setDraft(d => {
      if (!d) return d;
      const present = (d.certifications ?? []).some(c => c.name.toLowerCase() === item.name.toLowerCase());
      if (include && !present) return { ...d, certifications: [...(d.certifications ?? []), item] };
      if (!include && present) return { ...d, certifications: (d.certifications ?? []).filter(c => c.name.toLowerCase() !== item.name.toLowerCase()) };
      return d;
    });
  };

  const toggleLearn = (idx: number) => {
    if (!analysis || !draft) return;
    const item = analysis.newLearnings?.[idx];
    if (!item) return;
    const next = new Set(learnSel);
    const include = !next.has(idx);
    if (include) next.add(idx); else next.delete(idx);
    setLearnSel(next);
    setDraft(d => {
      if (!d) return d;
      const present = (d.learnings ?? []).some(l => l.title.toLowerCase() === item.title.toLowerCase());
      if (include && !present) return { ...d, learnings: [...(d.learnings ?? []), item] };
      if (!include && present) return { ...d, learnings: (d.learnings ?? []).filter(l => l.title.toLowerCase() !== item.title.toLowerCase()) };
      return d;
    });
  };

  // Lookups so the right pane can flag items that came from AI
  const aiSkillSet = useMemo(
    () => new Set((analysis?.newSkills ?? []).map(s => s.toLowerCase())),
    [analysis]
  );
  const aiCertSet = useMemo(
    () => new Set((analysis?.newCertifications ?? []).map(c => c.name.toLowerCase())),
    [analysis]
  );
  const aiLearnSet = useMemo(
    () => new Set((analysis?.newLearnings ?? []).map(l => l.title.toLowerCase())),
    [analysis]
  );

  const selectedCount = skillSel.size + certSel.size + learnSel.size;
  const nothingToAdd = analysis &&
    (analysis.newSkills?.length ?? 0) === 0 &&
    (analysis.newCertifications?.length ?? 0) === 0 &&
    (analysis.newLearnings?.length ?? 0) === 0;

  const handleApply = async () => {
    if (!analysis || !draft || selectedCount === 0) return;
    setApplying(true);
    setError(null);
    try {
      const payload: CvAnalysisResult = {
        ...analysis,
        newSkills: (analysis.newSkills ?? []).filter((_, i) => skillSel.has(i)),
        newCertifications: (analysis.newCertifications ?? []).filter((_, i) => certSel.has(i)),
        newLearnings: (analysis.newLearnings ?? []).filter((_, i) => learnSel.has(i)),
        updatedCv: draft,
      };
      const newVersion = await applyCvUpdate(employeeId, connectIds, payload, apiCall);
      onSuccess(newVersion.version);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to apply update');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 px-6 py-4 flex items-center justify-between text-white shadow-md">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center flex-shrink-0"
            title="Back to Profile"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold leading-tight">Update CV from {connects.length} Connect{connects.length === 1 ? '' : 's'}</h2>
            <p className="text-xs text-white/85 mt-0.5 truncate">
              Toggle items on the left · Edit your CV on the right · Save when ready
            </p>
          </div>
        </div>
        <button onClick={onClose} className="text-white/80 hover:text-white p-1">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {loading ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-3">
            <Loader2 className="w-7 h-7 animate-spin text-pink-500" />
            <p className="text-sm font-medium">Analyzing your Connects with AI…</p>
            <p className="text-xs text-slate-400">This usually takes 5-15 seconds.</p>
          </div>
        ) : error && !analysis ? (
          <div className="h-full flex items-center justify-center p-6">
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm max-w-lg">{error}</div>
          </div>
        ) : analysis && draft ? (
          <div className="h-full flex flex-col lg:flex-row">
            {/* LEFT PANE — selections */}
            <aside className="lg:w-[34%] lg:max-w-[460px] lg:min-w-[320px] border-r border-slate-200 bg-white overflow-y-auto">
              <div className="p-5 space-y-5">
                {analysis.summary && (
                  <div className="bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-100 rounded-xl p-4">
                    <p className="text-xs font-bold text-pink-600 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" /> AI summary
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed">{analysis.summary}</p>
                  </div>
                )}

                {nothingToAdd ? (
                  <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <Sparkles className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm font-medium text-slate-500">No new items detected</p>
                    <p className="text-xs mt-1">You can still review & edit the CV on the right, then save as a new version.</p>
                  </div>
                ) : (
                  <>
                    <SelectableSection
                      title="New skills"
                      icon={<Sparkles className="w-4 h-4 text-pink-600" />}
                      items={analysis.newSkills ?? []}
                      selected={skillSel}
                      onToggle={toggleSkill}
                      onSelectAll={() => {
                        const all = new Set((analysis.newSkills ?? []).map((_, i) => i));
                        // Mass-add: rebuild draft by ensuring all are present
                        setSkillSel(all);
                        setDraft(d => d ? { ...d, skills: dedupCi([...(d.skills ?? []), ...(analysis.newSkills ?? [])]) } : d);
                      }}
                      onClearAll={() => {
                        setSkillSel(new Set());
                        setDraft(d => d ? { ...d, skills: (d.skills ?? []).filter(s => !aiSkillSet.has(s.toLowerCase())) } : d);
                      }}
                      renderItem={(s) => <span className="text-sm font-medium text-slate-800">+ {s}</span>}
                      accent="pink"
                    />
                    <SelectableSection
                      title="New certifications"
                      icon={<Award className="w-4 h-4 text-purple-600" />}
                      items={analysis.newCertifications ?? []}
                      selected={certSel}
                      onToggle={toggleCert}
                      onSelectAll={() => {
                        setCertSel(new Set((analysis.newCertifications ?? []).map((_, i) => i)));
                        setDraft(d => {
                          if (!d) return d;
                          const have = new Set((d.certifications ?? []).map(c => c.name.toLowerCase()));
                          const adds = (analysis.newCertifications ?? []).filter(c => !have.has(c.name.toLowerCase()));
                          return { ...d, certifications: [...(d.certifications ?? []), ...adds] };
                        });
                      }}
                      onClearAll={() => {
                        setCertSel(new Set());
                        setDraft(d => d ? { ...d, certifications: (d.certifications ?? []).filter(c => !aiCertSet.has(c.name.toLowerCase())) } : d);
                      }}
                      renderItem={(c) => (
                        <span className="text-sm">
                          <span className="font-semibold text-slate-800">{c.name}</span>
                          {c.issuer && <span className="text-slate-500"> · {c.issuer}</span>}
                          {c.date && <span className="text-slate-400"> · {c.date}</span>}
                        </span>
                      )}
                      accent="purple"
                    />
                    <SelectableSection
                      title="New learnings"
                      icon={<GraduationCap className="w-4 h-4 text-indigo-600" />}
                      items={analysis.newLearnings ?? []}
                      selected={learnSel}
                      onToggle={toggleLearn}
                      onSelectAll={() => {
                        setLearnSel(new Set((analysis.newLearnings ?? []).map((_, i) => i)));
                        setDraft(d => {
                          if (!d) return d;
                          const have = new Set((d.learnings ?? []).map(l => l.title.toLowerCase()));
                          const adds = (analysis.newLearnings ?? []).filter(l => !have.has(l.title.toLowerCase()));
                          return { ...d, learnings: [...(d.learnings ?? []), ...adds] };
                        });
                      }}
                      onClearAll={() => {
                        setLearnSel(new Set());
                        setDraft(d => d ? { ...d, learnings: (d.learnings ?? []).filter(l => !aiLearnSet.has(l.title.toLowerCase())) } : d);
                      }}
                      renderItem={(l) => (
                        <div className="text-sm">
                          <p className="font-semibold text-slate-800">
                            {l.title}
                            {l.date && <span className="text-slate-400 font-normal"> · {l.date}</span>}
                          </p>
                          {l.description && <p className="text-slate-600 mt-0.5 line-clamp-2">{l.description}</p>}
                        </div>
                      )}
                      accent="indigo"
                    />
                  </>
                )}

                {/* Sources */}
                <div className="pt-2">
                  <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Sources</h4>
                  <ul className="space-y-1.5">
                    {connects.map(c => (
                      <li key={c.id} className="text-xs text-slate-600 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>

            {/* RIGHT PANE — editable CV */}
            <section className="flex-1 min-w-0 overflow-y-auto bg-slate-50">
              <div className="max-w-3xl mx-auto p-6 space-y-5">
                <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">Profile</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TextField label="Full name" value={draft.fullName} onChange={v => setDraft(d => d ? { ...d, fullName: v } : d)} />
                    <TextField label="Job title" value={draft.jobTitle} onChange={v => setDraft(d => d ? { ...d, jobTitle: v } : d)} />
                    <TextField label="Email" value={draft.email} onChange={v => setDraft(d => d ? { ...d, email: v } : d)} />
                    <TextField label="Phone" value={draft.phone} onChange={v => setDraft(d => d ? { ...d, phone: v } : d)} />
                    <TextField label="Location" value={draft.location} onChange={v => setDraft(d => d ? { ...d, location: v } : d)} className="sm:col-span-2" />
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">Summary</h3>
                  <textarea
                    value={draft.summary}
                    onChange={e => setDraft(d => d ? { ...d, summary: e.target.value } : d)}
                    rows={4}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200"
                  />
                </div>

                {/* Skills chips */}
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Skills</h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(draft.skills ?? []).map((s, i) => {
                      const isAi = aiSkillSet.has(s.toLowerCase());
                      return (
                        <span
                          key={i}
                          className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                            isAi ? 'bg-pink-50 text-pink-700 border-pink-200' : 'bg-slate-100 text-slate-700 border-slate-200'
                          }`}
                        >
                          {isAi && <Sparkles className="w-3 h-3" />}
                          {s}
                          <button
                            onClick={() => setDraft(d => d ? { ...d, skills: d.skills.filter((_, j) => j !== i) } : d)}
                            className="hover:text-red-600"
                            title="Remove"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <SkillInput onAdd={(s) => setDraft(d => d ? { ...d, skills: dedupCi([...(d.skills ?? []), s]) } : d)} />
                </div>

                {/* Experience */}
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Experience</h3>
                    <button
                      onClick={() => {
                        setDraft(d => d ? { ...d, experience: [...(d.experience ?? []), { ...emptyExp, bullets: [] }] } : d);
                        setExpandedExp(s => new Set([...Array.from(s), (draft.experience?.length ?? 0)]));
                      }}
                      className="text-xs font-semibold text-pink-600 hover:text-pink-700 inline-flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(draft.experience ?? []).map((exp, i) => (
                      <ExperienceCard
                        key={i}
                        exp={exp}
                        expanded={expandedExp.has(i)}
                        onToggle={() => setExpandedExp(s => {
                          const next = new Set(s);
                          if (next.has(i)) next.delete(i); else next.add(i);
                          return next;
                        })}
                        onChange={(next) => setDraft(d => d ? { ...d, experience: d.experience.map((x, j) => j === i ? next : x) } : d)}
                        onRemove={() => setDraft(d => d ? { ...d, experience: d.experience.filter((_, j) => j !== i) } : d)}
                      />
                    ))}
                    {(draft.experience ?? []).length === 0 && (
                      <p className="text-xs text-slate-400 italic">No experience added.</p>
                    )}
                  </div>
                </div>

                {/* Projects */}
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Projects</h3>
                    <button
                      onClick={() => {
                        setDraft(d => d ? { ...d, projects: [...(d.projects ?? []), structuredClone(emptyProject)] } : d);
                        setExpandedProj(s => new Set([...Array.from(s), (draft.projects?.length ?? 0)]));
                      }}
                      className="text-xs font-semibold text-pink-600 hover:text-pink-700 inline-flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(draft.projects ?? []).map((project, i) => (
                      <ProjectCard
                        key={i}
                        project={project}
                        expanded={expandedProj.has(i)}
                        onToggle={() => setExpandedProj(s => {
                          const next = new Set(s);
                          if (next.has(i)) next.delete(i); else next.add(i);
                          return next;
                        })}
                        onChange={(next) => setDraft(d => d ? { ...d, projects: (d.projects ?? []).map((x, j) => j === i ? next : x) } : d)}
                        onRemove={() => setDraft(d => d ? { ...d, projects: (d.projects ?? []).filter((_, j) => j !== i) } : d)}
                      />
                    ))}
                    {(draft.projects ?? []).length === 0 && (
                      <p className="text-xs text-slate-400 italic">No projects added.</p>
                    )}
                  </div>
                </div>

                {/* Achievements */}
                <ListEditor<CvAchievement>
                  title="Achievements"
                  items={draft.achievements ?? []}
                  empty={emptyAchievement}
                  onChange={(items) => setDraft(d => d ? { ...d, achievements: items } : d)}
                  render={(a, onChange) => (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <TextField compact label="Title" value={a.title} onChange={v => onChange({ ...a, title: v })} />
                      <TextField compact label="Description" value={a.description} onChange={v => onChange({ ...a, description: v })} />
                      <TextField compact label="Date" value={a.date} onChange={v => onChange({ ...a, date: v })} />
                    </div>
                  )}
                />

                {/* Certifications */}
                <ListEditor<CvCertification>
                  title="Certifications"
                  items={draft.certifications ?? []}
                  empty={emptyCert}
                  isAi={(c) => aiCertSet.has(c.name.toLowerCase())}
                  onChange={(items) => setDraft(d => d ? { ...d, certifications: items } : d)}
                  render={(c, onChange) => (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <TextField compact label="Name" value={c.name} onChange={v => onChange({ ...c, name: v })} />
                      <TextField compact label="Issuer" value={c.issuer} onChange={v => onChange({ ...c, issuer: v })} />
                      <TextField compact label="Date" value={c.date} onChange={v => onChange({ ...c, date: v })} />
                      <TextField compact label="Description" value={c.description ?? ''} onChange={v => onChange({ ...c, description: v })} className="sm:col-span-2" />
                      <TextField compact label="Credential ID" value={c.credentialId ?? ''} onChange={v => onChange({ ...c, credentialId: v })} />
                    </div>
                  )}
                />

                {/* Learnings */}
                <ListEditor<CvLearning>
                  title="Learnings"
                  items={draft.learnings ?? []}
                  empty={emptyLearn}
                  isAi={(l) => aiLearnSet.has(l.title.toLowerCase())}
                  onChange={(items) => setDraft(d => d ? { ...d, learnings: items } : d)}
                  render={(l, onChange) => (
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <TextField compact label="Title" value={l.title} onChange={v => onChange({ ...l, title: v })} className="sm:col-span-2" />
                        <TextField compact label="Date" value={l.date} onChange={v => onChange({ ...l, date: v })} />
                      </div>
                      <textarea
                        value={l.description}
                        onChange={e => onChange({ ...l, description: e.target.value })}
                        rows={2}
                        placeholder="Description"
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200"
                      />
                    </div>
                  )}
                />

                {/* Education */}
                <ListEditor<CvEducation>
                  title="Education"
                  items={draft.education ?? []}
                  empty={emptyEdu}
                  onChange={(items) => setDraft(d => d ? { ...d, education: items } : d)}
                  render={(e, onChange) => (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <TextField compact label="Degree" value={e.degree} onChange={v => onChange({ ...e, degree: v })} />
                      <TextField compact label="Institution" value={e.institution} onChange={v => onChange({ ...e, institution: v })} />
                      <TextField compact label="Location" value={e.location ?? ''} onChange={v => onChange({ ...e, location: v })} />
                      <TextField compact label="Score" value={e.score ?? ''} onChange={v => onChange({ ...e, score: v })} />
                      <TextField compact label="Year" value={e.year} onChange={v => onChange({ ...e, year: v })} />
                    </div>
                  )}
                />

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>
                )}
              </div>
            </section>
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-3 bg-white">
        <p className="text-xs text-slate-500">
          {nothingToAdd
            ? 'No AI suggestions to save'
            : `${selectedCount} AI item${selectedCount === 1 ? '' : 's'} selected`}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            disabled={applying}
            className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:text-slate-800 border border-slate-300 bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={loading || applying || !!error || !draft || selectedCount === 0}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-sm shadow-pink-500/20 disabled:opacity-50"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {applying ? 'Saving…' : 'Save as new version'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── helpers / small components ────────────────────────────────────────────

function dedupCi(arr: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of arr) {
    const k = s.toLowerCase();
    if (!seen.has(k)) { seen.add(k); out.push(s); }
  }
  return out;
}

function TextField({
  label, value, onChange, className, compact,
}: { label: string; value: string; onChange: (v: string) => void; className?: string; compact?: boolean }) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className={`block text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${compact ? 'mb-0.5' : 'mb-1'}`}>{label}</span>
      <input
        type="text"
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200"
      />
    </label>
  );
}

function SkillInput({ onAdd }: { onAdd: (s: string) => void }) {
  const [v, setV] = useState('');
  return (
    <div className="flex items-center gap-2">
      <input
        value={v}
        onChange={e => setV(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && v.trim()) { e.preventDefault(); onAdd(v.trim()); setV(''); }
        }}
        placeholder="Add a skill and press Enter"
        className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200"
      />
      <button
        onClick={() => { if (v.trim()) { onAdd(v.trim()); setV(''); } }}
        className="text-xs font-semibold px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1"
      >
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    </div>
  );
}

function ExperienceCard({
  exp, expanded, onToggle, onChange, onRemove,
}: {
  exp: CvExperience;
  expanded: boolean;
  onToggle: () => void;
  onChange: (next: CvExperience) => void;
  onRemove: () => void;
}) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 p-3 bg-slate-50">
        <button onClick={onToggle} className="text-slate-500 hover:text-slate-700">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-semibold text-slate-800 truncate">{exp.role || 'Untitled role'}</span>
          {exp.company && <span className="text-slate-500"> · {exp.company}</span>}
        </div>
        <button onClick={onRemove} className="text-slate-400 hover:text-red-600" title="Remove">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {expanded && (
        <div className="p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <TextField compact label="Role" value={exp.role} onChange={v => onChange({ ...exp, role: v })} />
            <TextField compact label="Company" value={exp.company} onChange={v => onChange({ ...exp, company: v })} />
            <TextField compact label="Start" value={exp.startDate} onChange={v => onChange({ ...exp, startDate: v })} />
            <TextField compact label="End" value={exp.endDate} onChange={v => onChange({ ...exp, endDate: v })} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Bullets</span>
              <button
                onClick={() => onChange({ ...exp, bullets: [...(exp.bullets ?? []), ''] })}
                className="text-xs text-pink-600 font-semibold inline-flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add bullet
              </button>
            </div>
            <ul className="space-y-1.5">
              {(exp.bullets ?? []).map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-slate-400 mt-2">•</span>
                  <input
                    value={b}
                    onChange={e => onChange({ ...exp, bullets: exp.bullets.map((x, j) => j === i ? e.target.value : x) })}
                    className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-pink-200"
                  />
                  <button
                    onClick={() => onChange({ ...exp, bullets: exp.bullets.filter((_, j) => j !== i) })}
                    className="text-slate-400 hover:text-red-600 mt-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectCard({
  project, expanded, onToggle, onChange, onRemove,
}: {
  project: CvProject;
  expanded: boolean;
  onToggle: () => void;
  onChange: (next: CvProject) => void;
  onRemove: () => void;
}) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 p-3 bg-slate-50">
        <button onClick={onToggle} className="text-slate-500 hover:text-slate-700">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-semibold text-slate-800 truncate">{project.name || 'Untitled project'}</span>
          {project.role && <span className="text-slate-500"> · {project.role}</span>}
        </div>
        <button onClick={onRemove} className="text-slate-400 hover:text-red-600" title="Remove">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {expanded && (
        <div className="p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <TextField compact label="Name" value={project.name} onChange={v => onChange({ ...project, name: v })} />
            <TextField compact label="Role" value={project.role} onChange={v => onChange({ ...project, role: v })} />
            <TextField compact label="Start" value={project.startDate ?? ''} onChange={v => onChange({ ...project, startDate: v })} />
            <TextField compact label="End" value={project.endDate ?? ''} onChange={v => onChange({ ...project, endDate: v })} />
          </div>
          <textarea
            value={project.description}
            onChange={e => onChange({ ...project, description: e.target.value })}
            rows={2}
            placeholder="Description"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200"
          />
          <TextField
            compact
            label="Tech stack (comma separated)"
            value={(project.techStack ?? []).join(', ')}
            onChange={v => onChange({ ...project, techStack: v.split(',').map(s => s.trim()).filter(Boolean) })}
          />
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Bullets</span>
              <button
                onClick={() => onChange({ ...project, bullets: [...(project.bullets ?? []), ''] })}
                className="text-xs text-pink-600 font-semibold inline-flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add bullet
              </button>
            </div>
            <ul className="space-y-1.5">
              {(project.bullets ?? []).map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-slate-400 mt-2">•</span>
                  <input
                    value={b}
                    onChange={e => onChange({ ...project, bullets: project.bullets.map((x, j) => j === i ? e.target.value : x) })}
                    className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-pink-200"
                  />
                  <button
                    onClick={() => onChange({ ...project, bullets: project.bullets.filter((_, j) => j !== i) })}
                    className="text-slate-400 hover:text-red-600 mt-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function ListEditor<T>({
  title, items, empty, onChange, render, isAi,
}: {
  title: string;
  items: T[];
  empty: T;
  onChange: (next: T[]) => void;
  render: (item: T, onChange: (next: T) => void) => React.ReactNode;
  isAi?: (item: T) => boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">{title}</h3>
        <button
          onClick={() => onChange([...items, structuredClone(empty)])}
          className="text-xs font-semibold text-pink-600 hover:text-pink-700 inline-flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => {
          const ai = isAi?.(item);
          return (
            <div
              key={i}
              className={`p-3 rounded-lg border ${ai ? 'border-pink-200 bg-pink-50/40' : 'border-slate-200 bg-white'}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  {ai && (
                    <div className="text-[10px] font-bold uppercase tracking-wider text-pink-600 mb-1 inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> AI added
                    </div>
                  )}
                  {render(item, (next) => onChange(items.map((x, j) => j === i ? next : x)))}
                </div>
                <button
                  onClick={() => onChange(items.filter((_, j) => j !== i))}
                  className="text-slate-400 hover:text-red-600 mt-1"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-xs text-slate-400 italic">None added.</p>
        )}
      </div>
    </div>
  );
}

function SelectableSection<T>({
  title, icon, items, selected, onToggle, onSelectAll, onClearAll, renderItem, accent,
}: {
  title: string;
  icon: React.ReactNode;
  items: T[];
  selected: Set<number>;
  onToggle: (i: number) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  renderItem: (item: T) => React.ReactNode;
  accent: 'pink' | 'purple' | 'indigo';
}) {
  if (items.length === 0) return null;
  const accentBg = accent === 'pink' ? 'bg-pink-50 border-pink-100'
    : accent === 'purple' ? 'bg-purple-50 border-purple-100'
    : 'bg-indigo-50 border-indigo-100';
  const accentText = accent === 'pink' ? 'text-pink-700'
    : accent === 'purple' ? 'text-purple-700'
    : 'text-indigo-700';

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">{title}</h3>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full bg-white border ${accentText}`}>
            {selected.size}/{items.length}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <button onClick={onSelectAll} className="text-slate-500 hover:text-slate-800 font-semibold">All</button>
          <span className="text-slate-300">·</span>
          <button onClick={onClearAll} className="text-slate-500 hover:text-slate-800 font-semibold">None</button>
        </div>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => {
          const isOn = selected.has(i);
          return (
            <li
              key={i}
              onClick={() => onToggle(i)}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                isOn ? accentBg : 'bg-white border-slate-200 opacity-60'
              }`}
            >
              <input
                type="checkbox"
                checked={isOn}
                onChange={() => onToggle(i)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 accent-pink-600 w-4 h-4 cursor-pointer"
              />
              <div className="flex-1 min-w-0">{renderItem(item)}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
